import { changeBaseItemIDToVariantID } from 'components/Sell/utils/common'
import Decimal from 'decimal.js'
import { keys, values } from 'lodash-es'
import {
    ComboViewModel,
    EntityModifiersMap,
    OrderItemViewModel,
    PriceListData,
    UsedModifiers,
    UsedVariants,
} from 'store/models/sellModels'
import { getItemPrice, getModifierOptionPrice } from 'utilities/price/getPrice'
import { round } from 'views/Sale/utils/orderCalculation/common'

class RecalculateOrder {
    orderItemView: OrderItemViewModel
    menuId: string
    priceListData: PriceListData
    comboChoicesMap: Record<string, ComboViewModel[]>

    entityModifiersMap: EntityModifiersMap

    constructor(
        orderItemView: OrderItemViewModel,
        menuId: string,
        priceListData: PriceListData,
        comboChoicesMap: Record<string, ComboViewModel[]>,
        entityModifiersMap: EntityModifiersMap,
    ) {
        this.orderItemView = orderItemView
        this.menuId = menuId
        this.priceListData = priceListData
        this.comboChoicesMap = comboChoicesMap
        this.entityModifiersMap = entityModifiersMap
    }

    process() {
        this.recalculateItemAndVariant()

        if (this.orderItemView.usedModifiers)
            this.recalculateModifierOption(this.orderItemView.usedModifiers)

        this.recalculateComboChoices()

        this.resetPriceChangedError()

        this.orderItemView.isItemUnavailable =
            values(this.orderItemView.error).flatMap((i) => i).length > 0
    }

    getOrderItemView() {
        return this.orderItemView
    }

    private recalculateItemAndVariant() {
        let itemId = changeBaseItemIDToVariantID(this.orderItemView).item.itemId

        this.orderItemView.menuId = this.menuId
        this.orderItemView.lineTotal = getItemPrice(
            itemId,
            this.menuId,
            this.priceListData,
        )
    }

    private recalculateModifierOption(usedModifiers: UsedModifiers) {
        let modLineTotal = new Decimal(0)
        keys(usedModifiers).forEach((bindId) => {
            const modOptions = usedModifiers[bindId]
            const hasAdditionalPrice =
                this.entityModifiersMap[bindId]?.hasAssociatedCost ?? true
            modOptions.forEach((modOption) => {
                modLineTotal = modLineTotal.plus(
                    hasAdditionalPrice
                        ? getModifierOptionPrice(
                              modOption.modifierOptionId!,
                              this.menuId,
                              this.priceListData,
                          )
                        : 0,
                )
                keys(modOption.subModifierOptions).forEach((bindId) => {
                    const subModifierOptions =
                        modOption.subModifierOptions[bindId]
                    const hasAdditionalPrice =
                        this.entityModifiersMap[bindId].hasAssociatedCost ??
                        true
                    subModifierOptions.forEach((subMod) => {
                        const subPrice = hasAdditionalPrice
                            ? getModifierOptionPrice(
                                  subMod.modifierOptionId,
                                  this.menuId,
                                  this.priceListData,
                              )
                            : 0
                        subMod.addPrice = subPrice
                        modLineTotal = modLineTotal.plus(subPrice)
                    })
                })
            })
        })

        this.orderItemView.lineTotal = round(
            new Decimal(this.orderItemView.lineTotal).plus(modLineTotal),
        ).toNumber()
    }

    private recalculateComboChoices() {
        keys(this.orderItemView.usedItems).forEach((pathItem) => {
            let item = this.orderItemView.usedItems[pathItem]

            if (item.usedModifiers)
                this.recalculateModifierOption(item.usedModifiers)

            if (item.usedVariants)
                this.recalculateComboChoice(
                    item.comboChoiceId,
                    item.usedVariants,
                )
        })
    }

    private recalculateComboChoice(
        comboChoiceID: string,
        usedVariants: UsedVariants,
    ) {
        let comboChoices = this.comboChoicesMap[
            this.orderItemView.item.itemId
        ].find((i) => i.comboChoiceID === comboChoiceID)

        let comboChoiceOptions =
            comboChoices?.comboItems
                .flatMap((i) => i.variantGroups)
                .flatMap((i) => i.variantOptions)
                .map((i) => ({
                    variantOptionId: i.variantOptionID,
                    addPrice: i.addPrice,
                })) ?? []

        values(usedVariants).forEach((variantOptionId) => {
            this.orderItemView.lineTotal = round(
                new Decimal(this.orderItemView.lineTotal).plus(
                    comboChoiceOptions.find(
                        (i) => i.variantOptionId === variantOptionId,
                    )?.addPrice ?? 0,
                ),
            ).toNumber()
        })
    }

    private resetPriceChangedError() {
        this.orderItemView.error = {
            'item-unavailable':
                this.orderItemView.error?.['item-unavailable'] ?? [],
            'price-changed': [],
            'time-unavailable':
                this.orderItemView.error?.['time-unavailable'] ?? [],
            'invalid-modifier-option':
                this.orderItemView.error?.['invalid-modifier-option'] ?? [],
        }
    }
}
export default RecalculateOrder
